import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { ConsumptionLocation, LocationAddress } from '../../types/meterReading';
import meterReadingService from '../../services/meterReadingService';

interface ConsumptionLocationFormProps {
  customerId: string;
  location?: ConsumptionLocation | null;
  onClose: () => void;
  onSaved: () => void;
}

const ConsumptionLocationForm: React.FC<ConsumptionLocationFormProps> = ({
  customerId,
  location,
  onClose,
  onSaved
}) => {
  const [formData, setFormData] = useState({
    locationName: '',
    address: {
      postalCode: '',
      city: '',
      street: '',
      houseNumber: '',
      building: '',
      floor: '',
      door: '',
      notes: ''
    } as LocationAddress,
    locationType: 'commercial' as 'residential' | 'commercial' | 'industrial',
    isActive: true
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (location) {
      setFormData({
        locationName: location.locationName,
        address: location.address,
        locationType: location.locationType,
        isActive: location.isActive
      });
    }
  }, [location]);

  const updateAddress = (field: keyof LocationAddress, value: string) => {
    setFormData({
      ...formData,
      address: {
        ...formData.address,
        [field]: value
      }
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaving(true);

    try {
      if (location) {
        // TODO: Implement update in service
        // await meterReadingService.updateConsumptionLocation(location.id, formData);
      } else {
        await meterReadingService.createConsumptionLocation(
          customerId,
          formData.locationName,
          formData.address,
          formData.locationType,
          formData.isActive
        );
      }
      onSaved();
    } catch (err: any) {
      setError(err.message || 'Hiba történt a mentés során');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose} />

        <div className="inline-block w-full max-w-2xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-gray-800 shadow-xl rounded-lg">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              {location ? 'Fogyasztási hely szerkesztése' : 'Új fogyasztási hely'}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="px-6 py-4">
            {error && (
              <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
              </div>
            )}

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                  Fogyasztási hely neve
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.locationName}
                  onChange={(e) => setFormData({ ...formData, locationName: e.target.value })}
                  className="block w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                  placeholder="pl. Fő telephely, Raktár, Gyelőállomás"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Add meg a fogyasztási hely egyedi nevét
                </p>
              </div>

              {/* Cím részletek */}
              <div className="bg-gray-50 dark:bg-gray-900/30 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
                  📍 Cím adatok
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Irányítószám */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Irányítószám <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.address.postalCode}
                      onChange={(e) => updateAddress('postalCode', e.target.value)}
                      className="block w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="1234"
                      maxLength={4}
                    />
                  </div>
                  
                  {/* Helység */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Helység/Város <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.address.city}
                      onChange={(e) => updateAddress('city', e.target.value)}
                      className="block w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Budapest"
                    />
                  </div>
                  
                  {/* Utca */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Utca <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.address.street}
                      onChange={(e) => updateAddress('street', e.target.value)}
                      className="block w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Péterfy Sándor utca"
                    />
                  </div>
                  
                  {/* Házszám */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Házszám <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.address.houseNumber}
                      onChange={(e) => updateAddress('houseNumber', e.target.value)}
                      className="block w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="12 vagy 12/A"
                    />
                  </div>
                  
                  {/* Épület */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Épület
                    </label>
                    <input
                      type="text"
                      value={formData.address.building || ''}
                      onChange={(e) => updateAddress('building', e.target.value)}
                      className="block w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="A épület"
                    />
                  </div>
                  
                  {/* Emelet */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Emelet
                    </label>
                    <input
                      type="text"
                      value={formData.address.floor || ''}
                      onChange={(e) => updateAddress('floor', e.target.value)}
                      className="block w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="3. emelet"
                    />
                  </div>
                  
                  {/* Ajtó */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Ajtó
                    </label>
                    <input
                      type="text"
                      value={formData.address.door || ''}
                      onChange={(e) => updateAddress('door', e.target.value)}
                      className="block w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="12"
                    />
                  </div>
                  
                  {/* Megjegyzés */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Megjegyzés (címhez)
                    </label>
                    <input
                      type="text"
                      value={formData.address.notes || ''}
                      onChange={(e) => updateAddress('notes', e.target.value)}
                      className="block w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="pl. Hátsó bejárat, 2. udvar"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                  Fogyasztási hely típusa
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <select
                  value={formData.locationType}
                  onChange={(e) => setFormData({ ...formData, locationType: e.target.value as any })}
                  className="block w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors cursor-pointer"
                >
                  <option value="residential">🏠 Lakossági</option>
                  <option value="commercial">🏪 Kereskedelmi</option>
                  <option value="industrial">🏭 Ipari</option>
                </select>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Válaszd ki a megfelelő kategóriát
                </p>
              </div>

              <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      type="checkbox"
                      id="isActive"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      className="h-5 w-5 text-blue-600 focus:ring-2 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded cursor-pointer"
                    />
                  </div>
                  <div className="ml-3">
                    <label htmlFor="isActive" className="font-medium text-sm text-gray-900 dark:text-white cursor-pointer">
                      Aktív fogyasztási hely
                    </label>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Az aktív helyek megjeleníthetők a listában és használhatók POD hozzárendeléshez
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                Mégse
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-6 py-2.5 text-sm font-semibold text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
              >
                {saving ? '⏳ Mentés...' : location ? '✔️ Mentés' : '➕ Létrehozás'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ConsumptionLocationForm;
